/**
 * LanguageProvider - i18n for UI constants only.
 * Per-user language, persisted in DB and localStorage. RTL for Arabic.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
  useMemo,
} from 'react';
import { languages, type LanguageKey } from '../i18n';

// Bootstrap user may be injected before hydration
declare global {
  interface Window {
    __BOOTSTRAP_USER__?: { language?: LanguageKey };
  }
}

function safeGetTranslation(
  obj: Record<string, any> | undefined,
  key: string
): string | undefined {
  if (!obj) return undefined;
  const parts = key.split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[part];
  }
  return typeof current === 'string' ? current : undefined;
}

export type LanguageContextValue = {
  lang: LanguageKey;
  setLang: (next: LanguageKey) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  dir: 'ltr' | 'rtl';
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function resolveInitialLang(): LanguageKey {
  if (typeof window === 'undefined') return 'he';
  const bootstrap = window.__BOOTSTRAP_USER__?.language;
  if (bootstrap === 'en' || bootstrap === 'ar' || bootstrap === 'he') return bootstrap;
  const stored = localStorage.getItem('lang');
  if (stored === 'en' || stored === 'ar' || stored === 'he') return stored;
  try {
    const client = JSON.parse(localStorage.getItem('client') || '{}');
    const effective = (client?.preferredLanguage ?? client?.language);
    if (effective === 'en' || effective === 'ar' || effective === 'he') return effective;
  } catch { /* ignore */ }
  return 'he';
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<LanguageKey>(resolveInitialLang);

  // Set dir and lang on document immediately (no flicker)
  useLayoutEffect(() => {
    const dir = (lang === 'ar' || lang === 'he') ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.documentElement.setAttribute('dir', dir);
  }, [lang]);

  const setLang = useCallback((next: LanguageKey) => {
    setLangState(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('lang', next);
      document.documentElement.lang = next;
      document.documentElement.dir = (next === 'ar' || next === 'he') ? 'rtl' : 'ltr';
    }
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const translations = languages[lang];
      let text = safeGetTranslation(translations, key);
      if (text == null) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[i18n] Missing key: "${key}"`);
        }
        text = key;
      }
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          text = (text as string).replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        });
      }
      return text as string;
    },
    [lang]
  );

  const dir = (lang === 'ar' || lang === 'he') ? 'rtl' : 'ltr';

  // Listen for app:setLanguage (from login/bootstrap)
  useEffect(() => {
    const handler = (e: CustomEvent<LanguageKey>) => {
      const next = e.detail;
      if (next === 'en' || next === 'ar' || next === 'he') {
        setLang(next);
      }
    };
    window.addEventListener('app:setLanguage', handler as EventListener);
    return () => window.removeEventListener('app:setLanguage', handler as EventListener);
  }, [setLang]);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang, t, dir }),
    [lang, setLang, t, dir]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export function useLang(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within LanguageProvider');
  return ctx;
}
